import Plugin from '../Plugin'

/**
 * GameSession – Client network plugin for the anti-cheat session token flow.
 *
 * Listens for:
 *   game_session_started  – Stores the token issued by GamerValidator on the server
 *   game_session_error    – Logs token request failures
 *   game_over_validated   – (Optional) Handles validated game-over responses
 *
 * The RuffleController calls `getToken()` after requesting a session, and
 * attaches the stored token to the `game_over` payload.
 */
export default class GameSession extends Plugin {

    constructor(network) {
        super(network)

        /** Pending token resolve callback (set by waitForToken) */
        this._tokenResolve = null

        /** Most recently issued session token (cleared after use) */
        this._currentToken = null

        /** GameId the current token belongs to */
        this._currentGameId = null

        this.events = {
            'game_session_started': this.onSessionStarted,
            'game_session_error': this.onSessionError,
            'game_over_validated': this.onGameOverValidated
        }
    }

    // ------------------------------------------------------------------
    // Server event handlers
    // ------------------------------------------------------------------

    /**
     * Server issued a session token.
     */
    onSessionStarted(args) {
        this._currentToken = args?.token ?? null
        this._currentGameId = args?.gameId ?? null

        // Resolve the pending promise (if RuffleController is waiting)
        if (this._tokenResolve) {
            this._tokenResolve(this._currentToken)
            this._tokenResolve = null
        }
    }

    /**
     * Server refused to issue a token.
     */
    onSessionError(args) {
        console.warn('[GameSession] Server refused session token:', args?.error)

        this._currentToken = null

        if (this._tokenResolve) {
            this._tokenResolve(null)
            this._tokenResolve = null
        }
    }

    /**
     * Server responded to a validated game_over (success or rejection).
     * The primary coin flow still goes through MiniGame.gameOver on the
     * server which sends the standard `game_over` response, so this is
     * mainly for diagnostics.
     */
    onGameOverValidated(args) {
        if (args && !args.success) {
            console.warn('[GameSession] Score submission rejected:', args.error)
        }
    }

    // ------------------------------------------------------------------
    // Public API (called by RuffleController / game scenes)
    // ------------------------------------------------------------------

    /**
     * Request a new session token from the server for the given game.
     *
     * @param {string} gameId – The game identifier (e.g. 'cart-surfer')
     * @param {number} [timeoutMs=5000] – Max time to wait for the server response
     * @returns {Promise<string|null>} The session token, or null on failure/timeout
     */
    requestToken(gameId, timeoutMs = 5000) {
        // Clear any stale state
        this._currentToken = null
        this._currentGameId = null

        // Send request to server
        this.network.send('start_game_session', { gameId })

        // Return a promise that resolves when `game_session_started` fires
        return new Promise((resolve) => {
            this._tokenResolve = resolve

            // Timeout safety – don't block the game forever
            setTimeout(() => {
                if (this._tokenResolve) {
                    console.warn('[GameSession] Token request timed out for', gameId)
                    this._tokenResolve = null
                    resolve(null)
                }
            }, timeoutMs)
        })
    }

    /**
     * Consume and return the current token (one-time use).
     * Returns null if no token is available.
     */
    consumeToken() {
        const token = this._currentToken
        this._currentToken = null
        this._currentGameId = null
        return token
    }
}
